import { Component, computed, input, signal } from '@angular/core';
import type { EChartsCoreOption } from 'echarts/core';

import { PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

echarts.use([PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

export interface PieChartSegment {
    label: string;
    value: number;
    color: string;
}

export interface PieChartCenterLabel {
    title: string;
    value: string | number;
    subtitle?: string;
}

export enum PieChartVariantEnum {
    SMALL = 'small',
    LARGE = 'large',
}

interface ComputedSegment extends PieChartSegment {
    percentage: number;
}

@Component({
    selector: 'app-ice-visuals-dashboard-pie-chart',
    imports: [NgxEchartsDirective],
    providers: [provideEchartsCore({ echarts })],
    templateUrl: './ice-visuals-dashboard-pie-chart.component.html',
    styleUrl: './ice-visuals-dashboard-pie-chart.component.scss',
})
export class IceVisualsDashboardPieChartComponent {
    segments = input.required<PieChartSegment[]>();
    loading = input<boolean>(false);
    variant = input<PieChartVariantEnum>(PieChartVariantEnum.LARGE);
    centerLabel = input<PieChartCenterLabel | null>(null);
    labelColumnHeader = input<string>('');
    valueColumnHeader = input<string>('Value');
    pieChartVariantEnum = PieChartVariantEnum;

    private static readonly EMPTY_STATE_COLOR = '#e5e7eb';

    highlightedIndex = signal(-1);

    totalValue = computed(() => this.segments().reduce((sum, segment) => sum + segment.value, 0));

    hasData = computed(() => this.totalValue() > 0);

    computedSegments = computed<ComputedSegment[]>(() => {
        const total = this.totalValue();
        return this.segments().map((segment) => ({
            ...segment,
            percentage: total > 0 ? Math.round((segment.value / total) * 100) : 0,
        }));
    });

    chartOptions = computed<EChartsCoreOption>(() => {
        const segmentData = this.computedSegments();
        const center = this.centerLabel();
        const hasData = this.hasData();
        const hasMultipleVisibleSegments = segmentData.filter((segment) => segment.value > 0).length > 1;

        const emptyStateData = [
            {
                name: '',
                value: 1,
                itemStyle: {
                    color: IceVisualsDashboardPieChartComponent.EMPTY_STATE_COLOR,
                },
                emphasis: {
                    disabled: true,
                },
            },
        ];

        const actualData = segmentData.map((segment) => ({
            name: segment.label,
            value: segment.value,
            itemStyle: {
                color: segment.color,
            },
            emphasis: segment.label
                ? undefined
                : {
                      disabled: true,
                  },
        }));

        return {
            tooltip: {
                trigger: 'item',
                backgroundColor: '#1D2939',
                borderColor: '#1D2939',
                borderRadius: 8,
                textStyle: {
                    color: '#FFFFFF',
                    fontSize: 13,
                    fontFamily: 'Lato',
                },
                formatter: (params: unknown) => {
                    const data = params as { name: string; value: number; percent: number; color: string };
                    if (!data.name) return '';
                    return `
                    <div style="display: flex; align-items: center; gap: 8px;">
                        <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${data.color};"></span>
                        <span>${data.name}</span>
                    </div>
                    <div style="margin-top: 4px; font-weight: 600;">
                        ${data.value} (${data.percent}%)
                    </div>
                `;
                },
            },
            legend: {
                show: false,
            },
            series: [
                {
                    type: 'pie',
                    radius: center !== null ? ['55%', '80%'] : ['0%', '80%'],
                    center: ['50%', '50%'],
                    avoidLabelOverlap: false,
                    padAngle: hasData && hasMultipleVisibleSegments ? (center !== null ? 2 : 1) : 0,
                    itemStyle: {
                        borderRadius: hasData ? (center !== null ? 4 : 2) : 0,
                    },
                    label: {
                        show: center !== null,
                        position: 'center',
                        formatter: () => {
                            if (!center) return '';
                            return [
                                `{title|${center.title}}`,
                                `{value|${center.value}}`,
                                center.subtitle ? `{subtitle|${center.subtitle}}` : '',
                            ]
                                .filter(Boolean)
                                .join('\n');
                        },
                        rich: {
                            title: {
                                color: '#6B7280',
                                fontSize: 13,
                                fontFamily: 'Lato',
                                fontWeight: 400,
                                lineHeight: 20,
                            },
                            value: {
                                color: '#374151',
                                fontSize: 36,
                                fontFamily: 'Lato',
                                fontWeight: 700,
                                lineHeight: 48,
                            },
                            subtitle: {
                                color: '#9CA3AF',
                                fontSize: 12,
                                fontFamily: 'Lato',
                                fontWeight: 400,
                                lineHeight: 18,
                            },
                        },
                    },
                    emphasis: {
                        scale: hasData,
                        scaleSize: 6,
                        itemStyle: {
                            shadowBlur: 10,
                            shadowOffsetX: 0,
                            shadowColor: 'rgba(0, 0, 0, 0.2)',
                        },
                    },
                    data: hasData ? actualData : emptyStateData,
                },
            ],
            animation: true,
            animationDuration: 800,
            animationEasing: 'cubicInOut',
        };
    });

    onChartMouseOver(event: { dataIndex?: number }): void {
        if (event.dataIndex !== undefined) {
            this.highlightedIndex.set(event.dataIndex);
        }
    }

    onChartMouseOut(): void {
        this.highlightedIndex.set(-1);
    }
}
